import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    FormLabelProps,
    Input,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { formatPartNumber } from '../../functions/formatPartNumber';
import {
    INVENTORY_FIELD_FOCUS_COLOR,
} from '../../utils/inventoryFieldStyles';

export interface InventoryAdditionalPartNumbersFieldProps {
    value: Array<string>;
    onChange: (partNumbers: Array<string>) => void;
    /** Primary PN — cannot be added as an alternate. */
    primaryPartNumber?: string;
    isDisabled?: boolean;
    label?: string;
    labelProps?: FormLabelProps;
    placeholder?: string;
}

/**
 * Freeform chip field for alternate (non-primary) part numbers.
 * Mirrors tags/bins chrome without a search API.
 */
const InventoryAdditionalPartNumbersField = ({
    value,
    onChange,
    primaryPartNumber = '',
    isDisabled = false,
    label = 'Additional Part Numbers',
    labelProps,
    placeholder = 'Add part number…',
}: InventoryAdditionalPartNumbersFieldProps) => {
    const [query, setQuery] = useState('');
    const [error, setError] = useState<string | undefined>();

    const tryAdd = (raw: string) => {
        const stripped = formatPartNumber(raw);
        if (!stripped) {
            setQuery('');
            setError(undefined);
            return;
        }
        const primary = formatPartNumber(primaryPartNumber);
        if (primary && stripped === primary) {
            setError('That is already the primary part number.');
            return;
        }
        if (value.some((v) => formatPartNumber(v) === stripped)) {
            setError('Part number already added.');
            return;
        }
        onChange([...value, stripped]);
        setQuery('');
        setError(undefined);
    };

    const remove = (pn: string) => {
        onChange(value.filter((v) => v !== pn));
        setError(undefined);
    };

    return (
        <FormControl>
            {label ? (
                <FormLabel fontSize="sm" {...labelProps}>
                    {label}
                </FormLabel>
            ) : null}
            <Flex
                align="center"
                gap={2}
                flexWrap="wrap"
                minW={0}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
                px={2}
                py={1}
                minH="40px"
                transition="border-color 0.15s ease, box-shadow 0.15s ease"
                _focusWithin={{
                    borderColor: INVENTORY_FIELD_FOCUS_COLOR,
                    boxShadow: 'none',
                }}
            >
                {value.map((pn) => (
                    <Tag
                        key={pn}
                        size="sm"
                        borderRadius="md"
                        bg="gray.100"
                        color="gray.700"
                        flexShrink={0}
                    >
                        <TagLabel fontFamily="mono">{pn}</TagLabel>
                        {!isDisabled && (
                            <TagCloseButton
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => remove(pn)}
                            />
                        )}
                    </Tag>
                ))}
                <Box flex={1} minW="120px">
                    <Input
                        size="sm"
                        variant="unstyled"
                        fontFamily="mono"
                        // Typed part numbers stay mono (matches the chips); the
                        // placeholder is prose, so it uses the Geist body font.
                        _placeholder={{ fontFamily: 'body', color: 'fg.muted' }}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setError(undefined);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                tryAdd(query);
                            }
                        }}
                        onBlur={() => {
                            if (query.trim()) tryAdd(query);
                        }}
                        placeholder={value.length === 0 ? placeholder : 'Add another…'}
                        isDisabled={isDisabled}
                    />
                </Box>
            </Flex>
            {error ? (
                <Text fontSize="xs" color="red.500" mt={1}>
                    {error}
                </Text>
            ) : null}
        </FormControl>
    );
};

export default InventoryAdditionalPartNumbersField;
